/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { ServiceData } from '@/types/sanity';
import { format } from 'date-fns';
import { Separator } from '@/components/ui/separator';
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Lock, Loader2 } from 'lucide-react';

interface PaymentSummaryProps {
	service: ServiceData;
	bookingData: {
		fullName: string;
		phone: string;
		email: string;
		deliveryMode: 'online' | 'inPerson';
		location: {
			city: string;
			state: string;
			country: string;
		};
	};
	selectedDate: Date;
	selectedTime: string;
	onBack: () => void;
	onPayment: (success: boolean, message?: string) => void;
	isSubmitting: boolean;
}

export function PaymentSummary({
	service,
	bookingData,
	selectedDate,
	selectedTime,
	onBack,
	onPayment,
	isSubmitting,
}: PaymentSummaryProps) {
	// Determine which price to use for payment
	const priceToDisplay = service.requiresConsultation
		? service.consultationPrice
		: service.inPersonPrice && bookingData.deliveryMode === 'inPerson'
			? service.inPersonPriceValue
			: service.price;

	// Include "consultation" text if applicable
	const serviceFeeLabel = service.requiresConsultation
		? 'Initial Consultation Fee'
		: 'Service Fee';

	// Set duration to 45 minutes for consultation services
	const displayDuration = service.requiresConsultation
		? '45 minutes'
		: service.duration === 0
			? 'Tailored'
			: service.duration + ' ' + service.format;

	const handlePaystackPayment = () => {
		// Ensure we're removing any prior class in case of multiple attempts
		document.body.classList.remove('paystack-active');
		// Then add class to body to help with paystack z-index handling
		document.body.classList.add('paystack-active');

		// Add small delay to ensure CSS changes are applied
		setTimeout(() => {
			// Create a unique reference with the FULL service ID, ensuring proper format
			// Use the complete service ID without additional dashes
			const reference = `${service._id}-${Date.now()}`;

			// Save booking data to localStorage to retrieve after payment
			localStorage.setItem(
				'yaap_pending_booking',
				JSON.stringify({
					service,
					bookingData,
					selectedDate:
						selectedDate.getFullYear() +
						'-' +
						String(selectedDate.getMonth() + 1).padStart(2, '0') +
						'-' +
						String(selectedDate.getDate()).padStart(2, '0'),
					selectedTime,
					reference,
					expectedAmount: priceToDisplay,
				}),
			);

			// Use the priceToDisplay variable that already handles delivery mode correctly
			const paymentAmount = priceToDisplay;

			// Initialize Paystack payment
			const handler = (window as any).PaystackPop.setup({
				key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY, // Using Vite env variable
				email: bookingData.email,
				amount: Number(paymentAmount) * 100, // Ensure proper format
				currency: 'ZAR',
				ref: reference,
				metadata: {
					custom_fields: [
						{
							display_name: 'Service ID',
							variable_name: 'service_id',
							value: service.slug,
						},
						{
							display_name: 'Service Name',
							variable_name: 'service_name',
							value: service.title,
						},
						{
							display_name: 'Customer Name',
							variable_name: 'customer_name',
							value: bookingData.fullName,
						},
						{
							display_name: 'Delivery Mode',
							variable_name: 'delivery_mode',
							value:
								bookingData.deliveryMode === 'inPerson'
									? 'In Person'
									: 'Online',
						},
					],
				},
				label: service.title,
				description: service.requiresConsultation
					? `Initial Consultation - 60 minutes`
					: `${service.frequency} - ${
							service.duration === 0
								? 'Tailored'
								: service.duration + ' ' + service.format
						} (${bookingData.deliveryMode === 'inPerson' ? 'In Person' : 'Online'})`,
				callback: function (response) {
					document.body.classList.remove('paystack-active');
					notifyPaymentSuccess(response.reference);
				},
				onClose: function () {
					document.body.classList.remove('paystack-active');
				},
			});

			handler.openIframe();
		}, 100);
	};

	const notifyPaymentSuccess = async (reference: string) => {
		try {
			// Get the stored booking data with expected amount
			const storedBooking = JSON.parse(
				localStorage.getItem('yaap_pending_booking') || '{}',
			);

			// Call a Netlify function to verify payment and perform other tasks
			const baseUrl =
				process.env.NODE_ENV === 'production'
					? 'https://yaap.co.za/.netlify/functions'
					: '/.netlify/functions';

			const response = await fetch(`${baseUrl}/verify-payment`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					reference,
					expectedAmount: storedBooking.expectedAmount, // Send the expected amount
					deliveryMode: storedBooking.bookingData?.deliveryMode, // Send delivery mode
				}),
			});

			const result = await response.json();

			// Proceed with appropriate flow based on payment result
			if (result.success) {
				onPayment(true);
			} else {
				onPayment(false, result.message || 'Payment verification failed');
			}
		} catch (error) {
			console.error('Failed to verify payment:', error);
			onPayment(false, 'Failed to verify payment');
		}
	};

	return (
		<div className='w-full max-w-3xl'>
			<Card className='w-full'>
				<CardHeader>
					<CardTitle>Booking Summary</CardTitle>
				</CardHeader>
				<CardContent className='space-y-6'>
					{/* Service Details */}
					<div className='space-y-2'>
						<h3 className='font-semibold'>{service.title}</h3>
						<p className='text-sm text-gray-500'>{service.description}</p>
						<div className='flex items-center text-sm text-gray-500'>
							<span className='mr-2'>
								{format(selectedDate, 'EEEE, MMMM d, yyyy')}
							</span>
							<span>at {selectedTime}</span>
						</div>
						<div className='text-sm text-gray-500'>
							Duration: {displayDuration}
						</div>
					</div>

					<Separator />

					{/* Personal Information */}
					<div className='space-y-2'>
						<h3 className='font-semibold'>Your Information</h3>
						<p className='text-sm text-gray-500'>{bookingData.fullName}</p>
						<p className='text-sm text-gray-500'>{bookingData.email}</p>
						<p className='text-sm text-gray-500'>{bookingData.phone}</p>
					</div>

					<Separator />

					{/* Payment Details */}
					<div className='space-y-2'>
						<h3 className='font-semibold'>Payment Details</h3>
						<div className='flex justify-between'>
							<span className='text-sm text-gray-500'>{serviceFeeLabel}</span>
							<span className='text-sm font-medium'>
								ZAR {Number(priceToDisplay).toFixed(2)}
							</span>
						</div>
						<div className='flex justify-between font-bold text-lg'>
							<span>Total</span>
							<span>ZAR {Number(priceToDisplay).toFixed(2)}</span>
						</div>
					</div>
				</CardContent>
				<CardFooter className='flex flex-col items-stretch space-y-4'>
					<div className='flex justify-between'>
						<Button variant='outline' onClick={onBack} disabled={isSubmitting}>
							Back
						</Button>
						<Button
							className='bg-[#fbb040] hover:bg-[#f8c880]'
							onClick={handlePaystackPayment}
							disabled={isSubmitting}
						>
							{isSubmitting ? (
								<>
									<Loader2 className='mr-2 h-4 w-4 animate-spin' />
									Processing...
								</>
							) : (
								'Confirm & Pay'
							)}
						</Button>
					</div>
					<div className='flex items-center justify-center text-xs text-gray-500'>
						<Lock size={12} className='mr-1' />
						<span>Secure payment powered by Paystack</span>
					</div>
				</CardFooter>
			</Card>
		</div>
	);
}
